import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Intercepte les flux de recovery / invite Supabase et garantit
 * que l'utilisateur atterrit sur /set-password (en préservant le slug
 * d'établissement s'il est présent dans l'URL).
 *
 * Stratégies (cumulatives) :
 * 1. Listener `onAuthStateChange` sur l'event `PASSWORD_RECOVERY` —
 *    déclenché par Supabase dès qu'il a parsé le hash de l'URL.
 * 2. Détection synchrone du hash au tout premier render (fallback
 *    pour les cas où Supabase n'a pas encore traité l'URL).
 */
export const RecoveryFlowHandler = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasHandledRef = useRef(false);

  // Détermine si on est déjà sur une page set-password (avec ou sans slug)
  const isOnSetPasswordPage = (pathname: string) =>
    pathname === "/set-password" || pathname.endsWith("/set-password");

  // Construit la cible /set-password en préservant le slug si présent
  const buildSetPasswordPath = (currentPath: string, hash: string) => {
    // Format attendu : /:slug/... → on récupère le premier segment
    const segments = currentPath.split("/").filter(Boolean);
    const knownNonSlugRoutes = new Set([
      "set-password",
      "forgot-password",
      "login",
      "signup",
      "verify-email",
      "dashboard",
      "admin",
      "onboarding",
      "demo",
      "contact",
      "merci",
      "legal",
      "blog",
      "pricing",
      "unsubscribe",
      "unsubscribe-lead",
      "comparatif",
      "lausanne",
      "geneve",
      "fribourg",
      "neuchatel",
      "sion",
      "offre",
      "sentry-example-page",
    ]);

    const firstSegment = segments[0];
    if (firstSegment && !knownNonSlugRoutes.has(firstSegment)) {
      return `/${firstSegment}/set-password${hash}`;
    }
    return `/set-password${hash}`;
  };

  const redirectToSetPassword = (hash: string) => {
    if (hasHandledRef.current) return;
    if (isOnSetPasswordPage(location.pathname)) {
      hasHandledRef.current = true;
      return;
    }
    hasHandledRef.current = true;
    sessionStorage.setItem("password_recovery_pending", "true");
    const target = buildSetPasswordPath(location.pathname, hash);
    console.log("[RecoveryFlowHandler] Redirecting to:", target);
    navigate(target, { replace: true });
  };

  // Fallback synchrone : détecte le hash au premier render
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("type=invite")) {
      console.log("[RecoveryFlowHandler] Recovery hash detected on mount");
      sessionStorage.setItem("password_recovery_pending", "true");
      if (!isOnSetPasswordPage(location.pathname)) {
        const target = buildSetPasswordPath(location.pathname, hash);
        console.log("[RecoveryFlowHandler] Redirecting (mount) to:", target);
        navigate(target, { replace: true });
        hasHandledRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listener officiel Supabase pour PASSWORD_RECOVERY
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        console.log("[RecoveryFlowHandler] PASSWORD_RECOVERY event received");
        redirectToSetPassword(window.location.hash || "");
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return <>{children}</>;
};
